/**
 * mood.ts — Mood widget + /mood trend chart — Phase 173
 *
 * Home widget: emoji tap → AJAX-POST to data-log-url → {success:true} → collapse to done state.
 * /mood page: Highcharts spline chart from mood_series server var + backfill form.
 * All mutations POST to signal URL read from dataset.logUrl (never hardcode ?do=).
 * Bootstrap imports directly — NOT window.bootstrap (silent no-op — feedback_bootstrap_not_on_window).
 * No per-chart time: block — useUTC:false is set globally in src/index.ts.
 */

import * as Highcharts from 'highcharts';
import { getChartTheme } from './utils/chartTheme';

// Populated by Mood/default.latte via {json_encode($trendSeries)|noescape}
declare const mood_series: Array<{ x: string; y: number }>;
// Populated by Mood/default.latte via {$logUrl} (Latte JS-context auto-escapes the URL string)
declare const mood_log_url: string;

const EMOJI_MAP: Record<number, string> = {
    1: '😞',
    2: '😕',
    3: '😐',
    4: '🙂',
    5: '😄',
};

const LABEL_MAP: Record<number, string> = {
    1: 'Very low',
    2: 'Low',
    3: 'Neutral',
    4: 'Good',
    5: 'Great',
};

export default {

    _inited: false,

    init(): void {
        // Idempotent init guard — prevent double-bind if init() is called twice (goals.ts pattern)
        if (this._inited) return;
        this._inited = true;

        this._initWidget();
        this._initBackfill();
        this._initTrendChart();
    },

    // ─── Home dashboard widget ────────────────────────────────────────────────

    _initWidget(): void {
        const widget = document.getElementById('mood-widget');
        if (!widget) return;  // not on a page with the mood widget

        const logUrl = widget.dataset.logUrl ?? '';
        if (!logUrl) return;

        const picker   = widget.querySelector<HTMLElement>('.mood-picker');
        const done     = widget.querySelector<HTMLElement>('.mood-done');
        const noteArea = widget.querySelector<HTMLElement>('.js-mood-note-area');
        const noteToggle  = widget.querySelector<HTMLButtonElement>('.js-mood-note-toggle');
        const noteExpand  = widget.querySelector<HTMLElement>('.js-mood-note-expand');
        const noteTextarea = widget.querySelector<HTMLTextAreaElement>('.js-mood-note');
        const noteCounter = widget.querySelector<HTMLElement>('.js-mood-note-counter');
        const submitBtn   = widget.querySelector<HTMLButtonElement>('.js-mood-submit');
        const submitLabel = widget.querySelector<HTMLElement>('.js-mood-submit-label');
        const spinner     = widget.querySelector<HTMLElement>('.js-mood-spinner');
        const errorEl     = widget.querySelector<HTMLElement>('.js-mood-error');
        const changeBtn   = widget.querySelector<HTMLButtonElement>('.js-mood-change');

        let selectedScore: number | null = null;

        // Face buttons — tap to select; tap-without-note posts immediately
        widget.querySelectorAll<HTMLButtonElement>('.js-mood-face').forEach(btn => {
            btn.addEventListener('click', () => {
                const score = parseInt(btn.dataset.score ?? '0', 10);
                if (!score) return;

                selectedScore = score;

                // Accent ring on selected, clear others
                widget.querySelectorAll<HTMLButtonElement>('.js-mood-face').forEach(b => {
                    b.style.outline = b === btn ? '2px solid var(--ksat-accent)' : '';
                    b.style.borderRadius = b === btn ? '50%' : '';
                    b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
                });

                // Show note area after selection
                if (noteArea) noteArea.classList.remove('d-none');

                // Fast path: if note area is NOT expanded, POST immediately (< 5s path, D-04)
                const noteVisible = noteExpand && !noteExpand.classList.contains('d-none');
                if (!noteVisible) {
                    this._postMood(logUrl, score, '', widget);
                }
            });
        });

        // Note toggle: expand/collapse the textarea
        if (noteToggle && noteExpand) {
            noteToggle.addEventListener('click', () => {
                noteExpand.classList.toggle('d-none');
                if (!noteExpand.classList.contains('d-none') && noteTextarea) {
                    noteTextarea.focus();
                }
            });
        }

        // Note counter
        if (noteTextarea && noteCounter) {
            noteTextarea.addEventListener('input', () => {
                noteCounter.textContent = `${noteTextarea.value.length} / 280`;
            });
        }

        // Submit button: posts score + note
        if (submitBtn) {
            submitBtn.addEventListener('click', () => {
                if (!selectedScore) return;
                const note = noteTextarea?.value ?? '';
                this._postMood(logUrl, selectedScore, note, widget);
            });
        }

        // "change" link in done state: re-expand the picker, reset to a clean reusable state
        if (changeBtn && picker && done) {
            changeBtn.addEventListener('click', () => {
                done.classList.add('d-none');
                picker.classList.remove('d-none');
                selectedScore = null;
                // Clear any selection/post state left from a prior log so the picker works again
                widget.querySelectorAll<HTMLButtonElement>('.js-mood-face').forEach(b => {
                    b.disabled = false;
                    b.style.outline = '';
                    b.style.borderRadius = '';
                    b.setAttribute('aria-pressed', 'false');
                });
                if (noteArea)     noteArea.classList.add('d-none');
                if (noteExpand)   noteExpand.classList.add('d-none');
                if (noteTextarea) noteTextarea.value = '';
                if (noteCounter)  noteCounter.textContent = '0 / 280';
                if (submitBtn)    submitBtn.disabled = false;
                if (submitLabel)  submitLabel.textContent = 'Log Mood';
                if (spinner)      spinner.classList.add('d-none');
                if (errorEl)      errorEl.classList.add('d-none');
            });
        }
    },

    _postMood(url: string, score: number, note: string, widget: HTMLElement): void {
        const day = new Date().toISOString().slice(0, 10);  // today YYYY-MM-DD
        this._submitPost(url, score, day, note, {
            onStart: () => {
                // Disable all face buttons + submit
                widget.querySelectorAll<HTMLButtonElement>('.js-mood-face').forEach(b => { b.disabled = true; });
                const submitBtn   = widget.querySelector<HTMLButtonElement>('.js-mood-submit');
                const submitLabel = widget.querySelector<HTMLElement>('.js-mood-submit-label');
                const spinner     = widget.querySelector<HTMLElement>('.js-mood-spinner');
                const errorEl     = widget.querySelector<HTMLElement>('.js-mood-error');
                if (submitBtn)   submitBtn.disabled = true;
                if (submitLabel) submitLabel.textContent = 'Saving…';
                if (spinner)     spinner.classList.remove('d-none');
                if (errorEl)     errorEl.classList.add('d-none');
            },
            onSuccess: () => {
                // Transition to done state (DOM swap — no page reload for today's widget)
                const picker = widget.querySelector<HTMLElement>('.mood-picker');
                const done   = widget.querySelector<HTMLElement>('.mood-done');
                if (picker) picker.classList.add('d-none');

                if (done) {
                    // Update done state with new score
                    const emojiSpan = done.querySelector<HTMLElement>('span[aria-label]');
                    if (emojiSpan) {
                        emojiSpan.textContent = EMOJI_MAP[score] ?? '';
                        emojiSpan.setAttribute('aria-label', LABEL_MAP[score] ?? '');
                    }
                    done.classList.remove('d-none');
                }
            },
            onError: (msg: string) => {
                const errorEl = widget.querySelector<HTMLElement>('.js-mood-error');
                const submitBtn   = widget.querySelector<HTMLButtonElement>('.js-mood-submit');
                const submitLabel = widget.querySelector<HTMLElement>('.js-mood-submit-label');
                const spinner     = widget.querySelector<HTMLElement>('.js-mood-spinner');
                if (errorEl) {
                    errorEl.textContent = msg || 'Could not save. Please try again.';
                    errorEl.classList.remove('d-none');
                }
                widget.querySelectorAll<HTMLButtonElement>('.js-mood-face').forEach(b => { b.disabled = false; });
                if (submitBtn)   submitBtn.disabled = false;
                if (submitLabel) submitLabel.textContent = 'Log Mood';
                if (spinner)     spinner.classList.add('d-none');
            },
        });
    },

    // ─── /mood backfill form ─────────────────────────────────────────────────

    _initBackfill(): void {
        const facesEl = document.querySelector<HTMLElement>('.js-mood-backfill-faces');
        if (!facesEl) return;  // not on /mood page

        const logUrl    = facesEl.dataset.logUrl ?? '';
        const dateInput = document.querySelector<HTMLInputElement>('.js-mood-backfill-date');
        const saveBtn   = document.querySelector<HTMLButtonElement>('.js-mood-bf-save');
        const saveLabel = document.querySelector<HTMLElement>('.js-mood-bf-save-label');
        const spinner   = document.querySelector<HTMLElement>('.js-mood-bf-spinner');
        const errorEl   = document.querySelector<HTMLElement>('.js-mood-bf-error');
        const noteToggle  = document.querySelector<HTMLButtonElement>('.js-mood-bf-note-toggle');
        const noteExpand  = document.querySelector<HTMLElement>('.js-mood-bf-note-expand');
        const noteTextarea = document.querySelector<HTMLTextAreaElement>('.js-mood-bf-note');
        const noteCounter = document.querySelector<HTMLElement>('.js-mood-bf-note-counter');

        let selectedScore: number | null = null;

        // Face buttons on /mood page
        facesEl.querySelectorAll<HTMLButtonElement>('.js-mood-bf-face').forEach(btn => {
            btn.addEventListener('click', () => {
                const score = parseInt(btn.dataset.score ?? '0', 10);
                if (!score) return;
                selectedScore = score;

                // Accent ring
                facesEl.querySelectorAll<HTMLButtonElement>('.js-mood-bf-face').forEach(b => {
                    b.style.outline = b === btn ? '2px solid var(--ksat-accent)' : '';
                    b.style.borderRadius = b === btn ? '50%' : '';
                    b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
                });

                // Enable save button
                if (saveBtn) saveBtn.disabled = false;
            });
        });

        // Note toggle
        if (noteToggle && noteExpand) {
            noteToggle.addEventListener('click', () => {
                noteExpand.classList.toggle('d-none');
            });
        }

        // Note counter
        if (noteTextarea && noteCounter) {
            noteTextarea.addEventListener('input', () => {
                noteCounter.textContent = `${noteTextarea.value.length} / 280`;
            });
        }

        // Save button
        if (saveBtn) {
            saveBtn.addEventListener('click', () => {
                if (!selectedScore) return;
                const day  = dateInput?.value ?? new Date().toISOString().slice(0, 10);
                const note = noteTextarea?.value ?? '';

                this._submitPost(logUrl, selectedScore, day, note, {
                    onStart: () => {
                        if (saveBtn)   saveBtn.disabled = true;
                        if (saveLabel) saveLabel.textContent = 'Saving…';
                        if (spinner)   spinner.classList.remove('d-none');
                        if (errorEl)   errorEl.classList.add('d-none');
                        facesEl.querySelectorAll<HTMLButtonElement>('.js-mood-bf-face').forEach(b => { b.disabled = true; });
                    },
                    onSuccess: () => {
                        // Reload so trend chart + recent list refresh (goals.ts page-reload pattern)
                        setTimeout(() => location.reload(), 600);
                    },
                    onError: (msg: string) => {
                        if (errorEl) {
                            errorEl.textContent = msg || 'Could not save. Please try again.';
                            errorEl.classList.remove('d-none');
                        }
                        if (saveBtn)   saveBtn.disabled = false;
                        if (saveLabel) saveLabel.textContent = 'Save';
                        if (spinner)   spinner.classList.add('d-none');
                        facesEl.querySelectorAll<HTMLButtonElement>('.js-mood-bf-face').forEach(b => { b.disabled = false; });
                    },
                });
            });
        }
    },

    // ─── Shared fetch POST helper ─────────────────────────────────────────────

    _submitPost(
        url: string,
        score: number,
        day: string,
        note: string,
        callbacks: {
            onStart: () => void;
            onSuccess: () => void;
            onError: (msg: string) => void;
        }
    ): void {
        callbacks.onStart();

        const body = new URLSearchParams();
        body.set('score', String(score));
        body.set('day',   day);
        body.set('note',  note);

        fetch(url, {
            method:  'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body:    body.toString(),
        })
        .then(r => r.json())
        .then((data: any) => {
            if (data.success) {
                callbacks.onSuccess();
            } else {
                callbacks.onError(data.message ?? 'Could not save. Please try again.');
            }
        })
        .catch(() => {
            callbacks.onError('Could not save. Please try again.');
        });
    },

    // ─── /mood trend chart ────────────────────────────────────────────────────

    _initTrendChart(): void {
        const el = document.getElementById('chart_mood_trend');
        if (!el) return;  // not on /mood page
        if (typeof mood_series === 'undefined') return;
        if (!mood_series.length) return;  // empty-state handled server-side in Latte

        const points = mood_series.map(p => [Date.parse(p.x), p.y]);

        Highcharts.chart('chart_mood_trend', Highcharts.merge(getChartTheme(), {
            chart: { type: 'spline', height: 320 },
            title: { text: undefined },
            legend: { enabled: false },
            xAxis: {
                type: 'datetime',
            },
            yAxis: {
                min: 1,
                max: 5,
                tickInterval: 1,
                title: { text: 'Mood (1–5)' },
                gridLineWidth: 0.1,
                plotLines: [{
                    value:     2.5,
                    dashStyle: 'Dash',
                    color:     '#6c757d',
                    width:     1,
                    label: {
                        text:  'Low mood threshold',
                    },
                }],
            },
            tooltip: { valueDecimals: 0 },
            series: [{
                name:         'Mood',
                color:        '#F9C74F',  // D-01: data-identity color — NOT accent, keep fixed
                data:         points,
                connectNulls: false,
                marker: { enabled: true, radius: 4 },
            }] as any,
            credits: { enabled: false },
        } as Highcharts.Options));
        // NOTE: No per-chart time: block — useUTC:false is set globally in src/index.ts (L44).
    },
};
